import React, { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Gift, Home, LogOut, ChevronDown, Menu, Clock } from 'lucide-react';
import { Avatar } from '../components/Avatar';

export const KidLayout: React.FC = () => {
    const { session, signOut } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setShowMobileMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isHomePage = location.pathname === '/kid/home';
    const isRewardsPage = location.pathname === '/kid/rewards';

    return (
        <div className="min-h-screen bg-purple-50/30 flex flex-col">
            <nav className="bg-white border-b border-purple-100 px-4 py-3 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto flex items-center justify-between relative h-10">
                    {/* Mobile/Tablet/Desktop Menu */}
                    <div className="flex items-center" ref={mobileMenuRef}>
                        <button
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                            className="p-2 text-slate-600 hover:text-purple-600 transition-colors"
                        >
                            <Menu size={20} />
                        </button>
                        
                        {showMobileMenu && (
                            <div className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50 min-w-[200px]">
                                <Link
                                    to="/kid/home"
                                    onClick={() => setShowMobileMenu(false)}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors ${
                                        isHomePage
                                            ? 'text-purple-600 bg-purple-50'
                                            : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    <Home size={16} />
                                    {t.nav.quests}
                                </Link>
                                <Link
                                    to="/kid/rewards"
                                    onClick={() => setShowMobileMenu(false)}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors ${
                                        isRewardsPage
                                            ? 'text-purple-600 bg-purple-50'
                                            : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    <Gift size={16} />
                                    {t.nav.rewards}
                                </Link>
                                <Link
                                    to="/kid/rewards/history"
                                    onClick={() => setShowMobileMenu(false)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors border-t border-slate-100"
                                >
                                    <Clock size={16} />
                                    {t.kidRewardHistory.title}
                                </Link>
                            </div>
                        )}
                    </div>
                    
                    {/* Centered Logo */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center">
                        <img 
                            src="/NestQ_logo_2026.png" 
                            alt="NestQ" 
                            className="h-8 rounded-lg"
                        />
                    </div>
                    
                    <div className="flex items-center gap-3 relative" ref={dropdownRef}>
                        <div className="text-right flex items-center">
                            <div className="text-sm font-black text-slate-800 leading-none">{session.user?.display_name?.split(' ')[0]}</div>
                        </div>
                        <button
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                        >
                            <Avatar name={session.user?.display_name || ''} size="sm" variant="kid" />
                            <ChevronDown size={16} className={`text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {showDropdown && (
                            <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50 min-w-[160px]">
                                <button
                                    onClick={() => {
                                        handleSignOut();
                                        setShowDropdown(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-2"
                                >
                                    <LogOut size={14} />
                                    {t.common.sign_out || 'Sign Out'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </nav>
            <main className="flex-1 max-w-4xl w-full mx-auto p-4 flex flex-col">
                <Outlet />
            </main>
        </div>
    );
};
